import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatRippleModule } from '@angular/material/core'
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatTabsModule } from '@angular/material/tabs'
import { MatTooltipModule } from '@angular/material/tooltip'
import { NotificationType } from '@generated/mordu.graphql'
import { TranslateModule } from '@ngx-translate/core'
import { NgScrollbarModule } from 'ngx-scrollbar'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { MappedNotification } from '../../core/interfaces/notification.interface'
import { ToolbarNotificationService } from '../../core/services/notifications/notifications.service'
import { PipesModule } from '../../pipes.module'

@Component({
  selector: 'app-notifications-dialog',
  templateUrl: './notifications-dialog.component.html',
  styleUrls: ['./notifications-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.Emulated,
  imports: [
    CommonModule,
    PipesModule,
    MatTabsModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatRippleModule,
    TranslateModule,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
    NgScrollbarModule
  ]
})
export class NotificationsDialogComponent implements OnInit {
  public notifications: MappedNotification[]
  public isLoadingNotifications: boolean
  public mountdoomUrl: string
  public mountdoom2Url: string
  public selectedTabs: Record<string, string>

  public constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly notificationsService: ToolbarNotificationService,
  ) {
    this.notifications = []
    this.isLoadingNotifications = true
    this.mountdoomUrl = data.environment.MOUNTDOOM_URL
    this.mountdoom2Url = data.environment.MOUNTDOOM_2_URL
    this.selectedTabs = {
      ticketCanceled: 'logs',
      maintenanceCanceled: 'logs',
      maintenanceCommented: 'comments',
      maintenanceOnNegotiation: 'general',
      maintenanceResponseDeadlineAtRisk: 'deadlines',
      maintenanceResponseDeadlineViolated: 'deadlines',
      maintenanceSolutionDeadlineAtRisk: 'deadlines',
      maintenanceSolutionDeadlineViolated: 'deadlines',
    }
  }

  public async ngOnInit(): Promise<void> {
    this.notifications = await this.notificationsService.listNotifications({
      before: null,
      take: 20,
      where: {
        typeIn: [
          NotificationType.MaintenanceCanceled,
          NotificationType.MaintenanceCommented,
          NotificationType.MaintenanceOnNegotiation,
          NotificationType.MaintenanceResponseDeadlineAtRisk,
          NotificationType.MaintenanceResponseDeadlineViolated,
          NotificationType.MaintenanceSolutionDeadlineAtRisk,
          NotificationType.MaintenanceSolutionDeadlineViolated,
          NotificationType.PendencyCompleted,
          NotificationType.PendencyCreated,
          NotificationType.PendencyReplied,
          NotificationType.PendencyTransferred,
          NotificationType.PendencyUpdated,
          NotificationType.QuotationCreated,
          NotificationType.StorageEmptyQuantity,
          NotificationType.StorageEntry,
          NotificationType.StorageExternalTransfer,
          NotificationType.StorageInternalTransfer,
          NotificationType.StorageMinQuantity,
          NotificationType.StorageMinQuantityAtRisk,
          NotificationType.StorageMinQuantityViolated,
          NotificationType.StorageRemoval,
          NotificationType.TicketCanceled,
          NotificationType.CompanyInviteAccepted,
          NotificationType.CompanyInviteRefused,
        ]
      }
    })

    this.isLoadingNotifications = false
  }

  public async markNotificationAsRead(notification: MappedNotification, event?: Event): Promise<void> {
    if (notification.readAt) {
      return
    }

    if (event) {
      event.stopPropagation()
    }

    notification.readAt = new Date().toISOString()
    await this.notificationsService.readNotification({
      id: notification.id
    })
  }

  public async markAllNotificationsAsRead(): Promise<void> {
    for (const notification of this.notifications.filter((notification) => !notification.readAt)) {
      notification.readAt = new Date().toISOString()
    }

    await this.notificationsService.readAllNotifications()
  }

  public async openNotification(notification: MappedNotification): Promise<void> {
    await this.markNotificationAsRead(notification)

    if (notification.type.includes('pendency')) {
      window.open(`${this.mountdoom2Url}/#/pendencias/${notification.data.pendency.id}`, '_blank')
    }

    if (notification.type.includes('storage')) {
      window.open(`${this.mountdoom2Url}/#/estoques?id=${notification.data.storageProduct.id}`, '_blank')
    }

    if (notification.type === 'quotationCreated') {
      window.open(`${this.mountdoomUrl}/#/orcamentos?id=${notification.data.quotation.id}`, '_blank')
    }

    if (notification.type === 'maintenanceCommented') {
      window.open(`${this.mountdoomUrl}/#/manutencoes?id=${notification.data.maintenance.id}&selectedTab=${this.selectedTabs[notification.type]}`, '_blank')
    }

    if (notification.type === 'ticketCommented') {
      window.open(`${this.mountdoom2Url}/#/solicitacoes?id=${notification.data.ticket.id}&selectedTab=${this.selectedTabs[notification.type]}`, '_blank')
    }
  }

  public trackByFn(index: number): number {
    return index
  }

  public openNotificationCenter(): void {
    window.open(`${this.mountdoom2Url}/#/notificacoes`, '_blank')
  }
}
